import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Droplet, Filter, TestTube, Shield } from 'lucide-react';
import Modal from '../shared/Modal';
import ContactForm from '../forms/ContactForm';

const SmartChoiceWaterCaseStudy: React.FC = () => {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);

  const handleBack = () => {
    navigate('/case-studies');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center text-[#00FFFF] hover:text-[#00FFFF]/80 transition-colors mb-8 mt-16 px-4 py-2 bg-black/20 rounded-lg border border-[#00FFFF]/20 hover:border-[#00FFFF]/40"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Case Studies
        </button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#00FFFF] to-purple-500 text-transparent bg-clip-text">
              Smart Choice Water
            </span>
          </h1>
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="px-3 py-1 bg-[#00FFFF]/10 text-[#00FFFF] rounded-full text-sm">
              Water Filtration
            </span>
            <span className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full text-sm">
              Public Health
            </span>
            <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm">
              Custom Solutions
            </span>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            Revolutionizing water filtration through innovative testing and custom filter solutions, ensuring clean and safe drinking water for households.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-12">
            {/* Partnership Section */}
            <motion.section
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">The Partnership</h2>
              <div className="space-y-4">
                <p className="text-gray-300">
                  SELF Labs Inc., led by Milan Cheeks, has partnered with Smart Choice Water, founded by Dustin Saulter, to bring innovative water filtration solutions directly to households.
                </p>
                <p className="text-gray-300">
                  This collaboration combines SELF Labs' technological expertise with Smart Choice Water's deep understanding of water quality issues, creating a powerful solution for clean water access.
                </p>
              </div>
            </motion.section>

            {/* Technology Section */}
            <motion.section
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">Innovative Technology</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#00FFFF]/10 rounded-lg">
                    <TestTube className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Advanced Water Testing</h3>
                    <p className="text-gray-300">
                      Utilizing Watman paper and comprehensive lab analysis to identify specific contaminants in household water supplies.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Filter className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Custom Filter Solutions</h3>
                    <p className="text-gray-300">
                      Combining structured water filters with Reverse Osmosis technology for ultimate water purification.
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>

          {/* Right Column */}
          <div className="space-y-12">
            {/* Process Section */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">How It Works</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <Droplet className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">1. Water Test Kit</h3>
                    <p className="text-gray-300">
                      Customers purchase a water test kit from smartchoicewater.com to begin the process.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <TestTube className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">2. Lab Analysis</h3>
                    <p className="text-gray-300">
                      Water samples are analyzed in certified labs to identify specific contaminants.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Shield className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">3. Custom Solution</h3>
                    <p className="text-gray-300">
                      A personalized water filter is designed and delivered to address the specific contaminants found.
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Impact Section */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">Impact & Future</h2>
              <div className="space-y-4">
                <p className="text-gray-300">
                  This partnership is making clean, safe drinking water accessible to more families across the country. The service is currently live, with plans to expand testing capabilities and reach more communities in need.
                </p>
                <a
                  href="https://smartchoicewater.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-[#00FFFF] text-black font-medium rounded-lg hover:bg-[#00FFFF]/90 transition-colors"
                >
                  Get Your Water Test Kit
                </a>
              </div>
            </motion.section>
          </div>
        </div>

        {/* Contact Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <button
            onClick={() => setShowContactModal(true)}
            className="px-8 py-4 bg-gradient-to-r from-[#00FFFF] to-purple-500 text-black font-medium rounded-lg hover:opacity-90 transition-opacity"
          >
            Contact Us About Water Solutions
          </button>
        </motion.div>
      </div>

      {/* Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Contact Us About Water Solutions"
      >
        <ContactForm />
      </Modal>
    </div>
  );
};

export default SmartChoiceWaterCaseStudy; 